import { useState, useEffect } from "react";
import { getAdminClients, createAdminClient, toggleClientStatus, revokeClientKey, regenerateClientKey,
         getAdminUsers, createAdminUser, pauseAdminUser, deleteAdminUser, updateUserApiKey,
         deleteAdminClient, updateAdminClient, getBillingData,
         getAdminReports, getAdminReportData, deleteAdminReport,
         downloadSavedExcel, downloadAdminExcel, saveExcelToHistory,
         getAdminBankStatements, downloadAdminBankExcel, bankStatementFilename } from "./api";
import { generateVatExcel, generateSalesExcel } from "./excelGenerator";
import ConfirmModal from "./ConfirmModal";
import AdminPageCount from "./AdminPageCount";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --bg:#02040e; --surface:#070d1f; --panel:#0a1225;
    --border:rgba(255,255,255,0.06); --border-blue:rgba(59,158,255,0.18);
    --ai-blue:#3b9eff; --ai-bright:#60b8ff; --ai-deep:#1a6fd4;
    --text:#eef4ff; --muted:#5a7a99; --muted-light:#8aabb8;
    --success:#22d3a8; --warning:#f59e0b; --error:#ff6b8a;
    --purple:#7c3aed; --teal:#0ea5e9;
  }
  html,body,#root{height:100%;background:var(--bg);color:var(--text);font-family:'Outfit',sans-serif;overflow-x:hidden;}

  .app{display:flex;min-height:100vh;}

  /* Sidebar */
  .sidebar{width:230px;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;position:fixed;top:0;left:0;bottom:0;z-index:100;transition:transform 0.3s;}
  .sb-brand{padding:20px;border-bottom:1px solid var(--border);}
  .sb-name{font-size:16px;font-weight:800;letter-spacing:-0.02em;}
  .sb-tag{font-size:9px;color:var(--error);letter-spacing:0.12em;text-transform:uppercase;margin-top:2px;}
  .sb-admin-badge{display:inline-flex;align-items:center;gap:5px;background:rgba(255,107,138,0.1);border:1px solid rgba(255,107,138,0.2);color:var(--error);padding:3px 10px;border-radius:20px;font-size:10px;font-weight:600;margin-top:6px;letter-spacing:0.06em;}
  .sb-dot{width:5px;height:5px;border-radius:50%;background:var(--error);animation:blink 1.5s ease infinite;}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:0.2}}
  .sb-nav{padding:12px 10px;flex:1;}
  .nav-section{font-size:9px;font-weight:600;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;padding:0 10px;margin:14px 0 6px;}
  .nav-item{display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:10px;cursor:pointer;font-size:13.5px;font-weight:500;color:var(--muted-light);transition:all 0.18s;margin-bottom:2px;border:1px solid transparent;}
  .nav-item:hover{background:rgba(59,158,255,0.07);color:var(--text);}
  .nav-item.active{background:rgba(59,158,255,0.12);color:var(--ai-bright);border-color:rgba(59,158,255,0.15);}
  .nav-icon{font-size:15px;width:20px;text-align:center;}
  .sb-footer{padding:14px 16px;border-top:1px solid var(--border);}
  .logout-btn{display:flex;align-items:center;gap:6px;padding:8px 12px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--muted);font-size:12px;font-family:'Outfit',sans-serif;cursor:pointer;width:100%;transition:all 0.2s;}
  .logout-btn:hover{border-color:var(--error);color:var(--error);}

  /* Main */
  .main{margin-left:230px;flex:1;display:flex;flex-direction:column;min-height:100vh;min-width:0;}
  .topbar{height:56px;background:var(--surface);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:sticky;top:0;z-index:50;}
  .topbar-title{font-size:16px;font-weight:700;letter-spacing:-0.01em;}
  .topbar-right{display:flex;align-items:center;gap:12px;}
  .hamburger{display:none;background:transparent;border:none;color:var(--text);font-size:20px;cursor:pointer;padding:4px;}
  .page{padding:24px;flex:1;min-width:0;}

  /* Stats grid */
  .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px;}
  .stat-card{background:var(--panel);border:1px solid var(--border);border-radius:16px;padding:18px 20px;animation:fadeUp 0.5s ease both;}
  .stat-card:nth-child(1){animation-delay:.05s}.stat-card:nth-child(2){animation-delay:.10s}
  .stat-card:nth-child(3){animation-delay:.15s}.stat-card:nth-child(4){animation-delay:.20s}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  .stat-label{font-size:11px;font-weight:600;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;margin-bottom:8px;}
  .stat-value{font-family:'JetBrains Mono',monospace;font-size:22px;font-weight:500;letter-spacing:-0.02em;}
  .stat-sub{font-size:11px;color:var(--muted);margin-top:4px;}
  .c-blue{color:var(--ai-blue)}.c-green{color:var(--success)}.c-warn{color:var(--warning)}.c-err{color:var(--error)}.c-purple{color:var(--purple)}

  /* Layout */
  .two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;}
  .three-col{display:grid;grid-template-columns:2fr 1fr;gap:16px;margin-bottom:20px;}
  .full-col{margin-bottom:20px;}

  /* Panel */
  .panel{background:var(--panel);border:1px solid var(--border);border-radius:16px;overflow:hidden;animation:fadeUp 0.5s ease 0.2s both;}
  .panel-header{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid var(--border);}
  .panel-title{font-size:13px;font-weight:700;color:var(--text);}
  .panel-body{padding:20px;}

  /* Table */
  .tbl{width:100%;border-collapse:collapse;font-size:13px;}
  .tbl th{text-align:left;padding:0 14px 10px;font-size:10px;font-weight:600;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;border-bottom:1px solid var(--border);}
  .tbl td{padding:12px 14px;border-bottom:1px solid rgba(255,255,255,0.04);color:var(--muted-light);vertical-align:middle;}
  .tbl tr:last-child td{border-bottom:none;}
  .tbl tr:hover td{background:rgba(59,158,255,0.04);color:var(--text);}
  .t-name{font-weight:600;color:var(--text);}
  .t-mono{font-family:'JetBrains Mono',monospace;font-size:12px;}
  .t-blue{font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--ai-blue);}

  /* Badges */
  .badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600;}
  .badge-dot{width:5px;height:5px;border-radius:50%;background:currentColor;}
  .b-active{background:rgba(34,211,168,0.1);color:var(--success);border:1px solid rgba(34,211,168,0.2);}
  .b-inactive{background:rgba(255,107,138,0.1);color:var(--error);border:1px solid rgba(255,107,138,0.2);}
  .b-trial{background:rgba(245,158,11,0.1);color:var(--warning);border:1px solid rgba(245,158,11,0.2);}
  .b-paid{background:rgba(59,158,255,0.1);color:var(--ai-blue);border:1px solid rgba(59,158,255,0.2);}

  /* Action buttons in table */
  .act-btn{padding:4px 10px;border-radius:7px;font-size:11px;font-weight:600;cursor:pointer;border:1px solid;transition:all 0.18s;font-family:'Outfit',sans-serif;background:transparent;}
  .act-enable{color:var(--success);border-color:rgba(34,211,168,0.3);}
  .act-enable:hover{background:rgba(34,211,168,0.1);}
  .act-disable{color:var(--error);border-color:rgba(255,107,138,0.3);}
  .act-disable:hover{background:rgba(255,107,138,0.1);}
  .act-view{color:var(--ai-blue);border-color:rgba(59,158,255,0.3);}
  .act-view:hover{background:rgba(59,158,255,0.1);}
  .act-edit{color:var(--warning);border-color:rgba(245,158,11,0.3);}
  .act-edit:hover{background:rgba(245,158,11,0.1);}

  /* Buttons */
  .btn{display:flex;align-items:center;justify-content:center;gap:8px;padding:10px 16px;border:none;border-radius:10px;font-family:'Outfit',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.22s;}
  .btn:hover{transform:translateY(-1px);}
  .btn-blue{background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue));color:#fff;box-shadow:0 4px 16px rgba(59,158,255,0.25);}
  .btn-ghost{background:rgba(255,255,255,0.05);color:var(--muted-light);border:1px solid var(--border);}
  .btn-ghost:hover{background:rgba(255,255,255,0.09);color:var(--text);}
  .btn-green{background:linear-gradient(135deg,#0f6e56,#22d3a8);color:#fff;}
  .btn-danger{background:rgba(239,68,68,0.12);color:var(--error);border:1px solid rgba(239,68,68,0.25);}
  .btn-danger:hover{background:rgba(239,68,68,0.2);}
  .btn-sm{padding:7px 12px;font-size:12px;border-radius:8px;}

  /* Key display */
  .key-box{background:rgba(59,158,255,0.05);border:1px solid var(--border-blue);border-radius:10px;padding:10px 14px;font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--ai-bright);letter-spacing:0.04em;display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;}
  .key-val{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .copy-btn{background:none;border:1px solid rgba(59,158,255,0.25);border-radius:6px;color:var(--ai-blue);font-size:11px;padding:3px 8px;cursor:pointer;flex-shrink:0;font-family:'Outfit',sans-serif;transition:all 0.2s;}
  .copy-btn:hover{background:rgba(59,158,255,0.1);}

  /* Form fields */
  .field{margin-bottom:12px;}
  .field label{display:block;font-size:10px;font-weight:600;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:6px;}
  .field input,.field select{width:100%;padding:10px 13px;background:rgba(59,158,255,0.04);border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Outfit',sans-serif;font-size:14px;outline:none;transition:border-color 0.2s,box-shadow 0.2s;}
  .field input:focus,.field select:focus{border-color:var(--ai-blue);box-shadow:0 0 0 3px rgba(59,158,255,0.1);}
  .field input::placeholder{color:rgba(90,122,153,0.5);}
  .field select{appearance:none;cursor:pointer;}
  .field select option{background:#0a1225;}

  /* VAT row */
  .v-row{display:flex;align-items:center;justify-content:space-between;padding:9px 0;border-bottom:1px solid var(--border);font-size:13px;}
  .v-row:last-child{border-bottom:none;}
  .v-lbl{color:var(--muted-light);}
  .v-val{font-family:'JetBrains Mono',monospace;font-size:13px;font-weight:500;}

  /* Usage bar */
  .usage-bar-bg{height:6px;background:rgba(255,255,255,0.06);border-radius:99px;overflow:hidden;margin-top:4px;}
  .usage-bar-fill{height:100%;border-radius:99px;transition:width 0.4s ease;}

  /* Modal */
  .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
  .modal{background:var(--panel);border:1px solid var(--border);border-radius:20px;padding:28px;width:100%;max-width:440px;box-shadow:0 30px 80px rgba(0,0,0,0.5);}
  .modal-title{font-size:17px;font-weight:700;margin-bottom:20px;}
  .modal-actions{display:flex;gap:10px;margin-top:20px;}

  /* Sidebar overlay */
  .sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:99;}
  .divider{height:1px;background:var(--border);margin:14px 0;}

  /* Activity feed */
  .activity-item{display:flex;gap:12px;padding:10px 0;border-bottom:1px solid var(--border);}
  .activity-item:last-child{border-bottom:none;}
  .act-dot{width:8px;height:8px;border-radius:50%;margin-top:5px;flex-shrink:0;}
  .act-text{font-size:13px;color:var(--muted-light);line-height:1.4;}
  .act-time{font-size:11px;color:var(--muted);margin-top:2px;}

  @media(max-width:900px){
    .sidebar{transform:translateX(-100%)}.sidebar.open{transform:translateX(0)}
    .sidebar-overlay.open{display:block}.main{margin-left:0}.hamburger{display:block}
    .stats-grid{grid-template-columns:repeat(2,1fr)}.two-col,.three-col{grid-template-columns:1fr}
    .tbl th:nth-child(4),.tbl td:nth-child(4),.tbl th:nth-child(5),.tbl td:nth-child(5){display:none}
    .page{padding:16px}
  }
  @media(max-width:480px){
    .stats-grid{grid-template-columns:1fr 1fr;gap:10px}.stat-value{font-size:18px}
    .topbar{padding:0 14px}.page{padding:12px}
    .tbl th:nth-child(3),.tbl td:nth-child(3){display:none}
  }
`;

// ── Initial state: all real data loaded from backend ──

const ADMIN_TABS = [
  {id:"overview",   icon:"⬡",  label:"Overview"},
  {id:"clients",    icon:"👥", label:"Clients"},
  {id:"apikeys",    icon:"🔑", label:"API Keys"},
  {id:"billing",    icon:"💳", label:"Billing"},
  {id:"pagecount",  icon:"📋", label:"Page Count"},
  {id:"reports",    icon:"📊", label:"Reports"},
  {id:"bankstatements", icon:"🏦", label:"Bank Statements"},
];

// Mirrors bank_extractor_router.BANK_DISPLAY_NAMES on the backend — used
// only for the bank filter dropdown on the Bank Statements tab.
const BANK_LABELS = {
  standard_bank:      "Standard Bank",
  standard_bank_loan: "Standard Bank (Finance Lease)",
  fnb:                "FNB Eswatini",
  eswatini_bank:      "Eswatini Bank",
  nedbank:            "Nedbank (Loan)",
  nedbank_current:    "Nedbank (Current)",
  sbs_loan:           "Swaziland Building Society (Loan)",
};

const fmt = n => `E ${Number(n).toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

function genKey(name) {
  const slug = name.toLowerCase().replace(/\s+/g,"-").slice(0,6);
  const rand = Math.random().toString(36).slice(2,10);
  return `vat-sk-${slug}-${rand}`;
}

export default function AdminDashboard({ onLogout, onViewModeChange }) {
  const [tab, setTab]         = useState("overview");
  const [sideOpen, setSide]   = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers]     = useState([]);
  const [showModal, setModal] = useState(false);
  const [showUserModal, setUserModal] = useState(false);
  const [editKeyUser, setEditKeyUser] = useState(null); // user being edited
  const [viewClient, setView] = useState(null);
  const [copied, setCopied]   = useState(null);
  const [adminError, setAdminError] = useState("");
  const [userError, setUserError]   = useState("");
  const [confirmModal, setConfirmModal] = useState(null);

  // New client form
  const [nName, setNName]     = useState("");
  const [nEmail, setNEmail]   = useState("");
  const [nCell, setNCell]     = useState("");
  const [nPlan, setNPlan]     = useState("Trial");

  // New user form
  const [uUsername, setUUsername] = useState("");
  const [uEmail, setUEmail]       = useState("");
  const [uPassword, setUPassword] = useState("client123");
  const [uCompany, setUCompany]   = useState("");
  const [uApiKey, setUApiKey]         = useState("");
  const [uKeyLabel, setUKeyLabel]     = useState("Primary Key");
  const [uExtractionPlan, setUExtractionPlan] = useState("Basic");

  // Edit key form
  const [ekKey, setEkKey]     = useState("");
  const [ekLabel, setEkLabel] = useState("");

  // Edit client form
  const [editClient, setEditClient]   = useState(null);
  const [ecName, setEcName]           = useState("");
  const [ecEmail, setEcEmail]         = useState("");
  const [ecCell, setEcCell]           = useState("");
  const [ecPlan, setEcPlan]           = useState("Trial");

  // Billing
  const [billing, setBilling]         = useState(null);
  const [billingMonth, setBillingMonth] = useState(new Date().toISOString().slice(0,7));

  // Reports
  const [reports, setReports]               = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [downloadingId, setDownloadingId]   = useState(null);
  const [downloadError, setDownloadError]   = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("all");
  const [reportSearch, setReportSearch]         = useState("");

  const [bankStatements, setBankStatements]         = useState([]);
  const [bankStatementsLoading, setBankStatementsLoading] = useState(false);
  const [bankDownloadingId, setBankDownloadingId]   = useState(null);
  const [bankDownloadError, setBankDownloadError]   = useState("");
  const [bankFilter, setBankFilter]                 = useState("all");
  const [bankSearch, setBankSearch]                 = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getAdminClients();
        setClients(result);
      } catch (err) {
        setAdminError(err.message || "Could not load clients.");
      }
      try {
        const uResult = await getAdminUsers();
        setUsers(uResult);
      } catch {
        // users table may be empty on first run — silently ignore
      }
      try {
        const bResult = await getBillingData();
        setBilling(bResult);
      } catch {
        // billing may be empty on first run
      }
      try {
        const rResult = await getAdminReports();
        setReports(rResult);
      } catch {
        // reports dir may be empty on first run
      }
      try {
        const bsResult = await getAdminBankStatements();
        setBankStatements(bsResult);
      } catch {
        // bank_statements table may be empty on first run
      }
    };
    load();
  }, []);

  const activeClients  = clients.filter(c=>c.status==="Active").length;
  const paidClients    = clients.filter(c=>c.plan==="Paid").length;
  const totalInvoices  = clients.reduce((s,c)=>s+(c.invoices||0),0);
  const activeKeys     = users.filter(u=>u.key_active && u.openai_key).length;

  const toggleStatus = async id => {
    try {
      const result = await toggleClientStatus(id);
      setClients(result);
    } catch (err) {
      setAdminError(err.message || "Failed to update client status.");
    }
  };

  const revokeKey = async id => {
    try {
      const result = await revokeClientKey(id);
      setClients(result);
    } catch (err) {
      setAdminError(err.message || "Failed to revoke API key.");
    }
  };

  const regenerateKey = async id => {
    try {
      const result = await regenerateClientKey(id);
      setClients(result);
    } catch (err) {
      setAdminError(err.message || "Failed to regenerate API key.");
    }
  };

  const addClient = async () => {
    if (!nName||!nEmail) return;
    try {
      const result = await createAdminClient({
        name: nName,
        email: nEmail,
        cell: nCell,
        plan: nPlan,
      });
      setClients(result);
      setNName(""); setNEmail(""); setNCell(""); setNPlan("Trial");
      setModal(false);
    } catch (err) {
      setAdminError(err.message || "Failed to add client.");
    }
  };

  const deleteClient = async id => {
    const c = clients.find(x => x.id === id);
    if (!c) return;
    setConfirmModal({ title:`Delete "${c.name}"?`, message:`This removes them from the server. Ensure all users belonging to this client are removed first.`, onConfirm: async ()=>{ try{ await deleteAdminClient(c.id); setClients(p=>p.filter(x=>x.id!==c.id)); }catch(e){ setAdminError(e.message); } } });
  };

  const openEditClient = c => {
    setEditClient(c);
    setEcName(c.name || "");
    setEcEmail(c.email || "");
    setEcCell(c.cell || "");
    setEcPlan(c.plan || "Trial");
  };

  const saveEditClient = async () => {
    if (!editClient) return;
    try {
      const result = await updateAdminClient(editClient.id, {
        name: ecName, email: ecEmail, cell: ecCell, plan: ecPlan,
      });
      setClients(result);
      setEditClient(null);
    } catch (err) {
      setAdminError(err.message || "Failed to update client.");
    }
  };

  const loadBilling = async (m) => {
    try {
      const result = await getBillingData(m);
      setBilling(result);
      setBillingMonth(m);
    } catch (err) {
      setAdminError(err.message || "Failed to load billing data.");
    }
  };

  const copyKey = (key, id) => {
    navigator.clipboard.writeText(key).then(()=>{
      setCopied(id);
      setTimeout(()=>setCopied(null), 2000);
    });
  };

  const addUser = async () => {
    if (!uUsername || !uCompany) return;
    setUserError("");
    try {
      const result = await createAdminUser({
        username:         uUsername,
        email:            uEmail,
        password:         uPassword || "client123",
        company_id:       parseInt(uCompany),
        openai_key:       uApiKey,
        gemini_key:       uApiKey,
        key_label:        uKeyLabel || "Primary Key",
        extraction_plan:  uExtractionPlan,
      });
      setUsers(result);
      setUUsername(""); setUEmail(""); setUPassword("client123");
      setUCompany(""); setUApiKey(""); setUKeyLabel("Primary Key"); setUExtractionPlan("Basic");
      setUserModal(false);
    } catch (err) {
      setUserError(err.message || "Failed to create user.");
    }
  };

  const pauseUser = async (uid) => {
    try {
      const result = await pauseAdminUser(uid);
      setUsers(result);
    } catch (err) {
      setAdminError(err.message || "Failed to pause user.");
    }
  };

  const deleteUser = async (uid, username) => {
    setConfirmModal({ title:`Delete User "${username}"?`, message:"This permanently removes the user account and cannot be reversed.", onConfirm: async ()=>{ try{ await deleteAdminUser(uid); const updated = await getAdminUsers(); setUsers(updated); }catch(e){ setAdminError(e.message); } } });
  };

  const openEditKey = (u) => {
    setEditKeyUser(u);
    setEkKey(u.openai_key || "");
    setEkLabel(u.key_label || "Primary Key");
  };

  const saveEditKey = async () => {
    if (!editKeyUser) return;
    try {
      const result = await updateUserApiKey(editKeyUser.id, { gemini_key: ekKey, key_label: ekLabel });
      setUsers(result);
      setEditKeyUser(null);
    } catch (err) {
      setAdminError(err.message || "Failed to update API key.");
    }
  };

  // Auto-refresh the Reports list every 15s while it's the active tab, so
  // newly-extracted reports show up without needing a manual reload.
  useEffect(() => {
    if (tab !== "reports") return;
    const interval = setInterval(() => { refreshReports(); }, 15000);
    return () => clearInterval(interval);
  }, [tab]);

  // Same auto-refresh pattern for Bank Statements.
  useEffect(() => {
    if (tab !== "bankstatements") return;
    const interval = setInterval(() => { refreshBankStatements(); }, 15000);
    return () => clearInterval(interval);
  }, [tab]);

  const REPORT_TYPE_META = {
    purchases:               { label: "Purchases",               color: "#3b9eff" },
    sales:                   { label: "Sales",                   color: "#22c55e" },
    multi_receipt_purchases: { label: "Multi-Receipt Purchases", color: "#a855f7" },
    multi_receipt_sales:     { label: "Multi-Receipt Sales",     color: "#eab308" },
    merged_purchases:        { label: "Merged Purchases",        color: "#f97316" },
    merged_sales:            { label: "Merged Sales",            color: "#f97316" },
  };
  const reportTypeMeta = (t) => REPORT_TYPE_META[t] || { label: t || "Purchases", color: "#94a3b8" };

  const refreshReports = async () => {
    setReportsLoading(true);
    try {
      const result = await getAdminReports();
      setReports(result);
    } catch (err) {
      setDownloadError(err.message || "Could not load reports.");
    } finally {
      setReportsLoading(false);
    }
  };

  const downloadReport = async (report) => {
    setDownloadingId(report.id);
    setDownloadError("");
    try {
      const rType = report.report_type || "purchases";
      const fname = `${reportTypeMeta(rType).label.replace(/\s+/g,"_")}_${report.company_name.replace(/\s+/g,"_")}_${report.created_at}.xlsx`;
      if (report.has_excel) {
        // Stored Excel exists — stream it directly (no regeneration needed).
        // True for every report type now: multi-receipt and merged reports
        // are saved with their Excel attached the moment they're generated.
        // Uses the admin-only route (require_admin, no ownership check) —
        // downloadSavedExcel hits the client-facing /api/my-reports/{id}/excel,
        // which 403s here because the report belongs to the client, not admin.
        await downloadAdminExcel(report.id, fname);
        return;
      }

      // No stored Excel yet — this can only happen for "purchases" or "sales"
      // reports, whose snapshot is saved at extraction time but whose Excel
      // is built in the browser and saved in a separate step. Regenerate it
      // here from the raw saved data, matching the report's actual type.
      if (rType !== "purchases" && rType !== "sales") {
        throw new Error("This report has no saved Excel file and can't be regenerated.");
      }
      const data = await getAdminReportData(report.id);
      const buffer = rType === "sales"
        ? await generateSalesExcel(data.sales_records || [], report.company_name, report.month || "", true)
        : await generateVatExcel(data.purchase_results || [], data.review_results || [], report.company_name, report.month || "", true);
      if (buffer) {
        try { await saveExcelToHistory(report.id, buffer); } catch {}
        setReports(prev => prev.map(r => r.id === report.id ? {...r, has_excel: true} : r));
        await downloadSavedExcel(report.id, fname);
      }
    } catch (err) {
      setDownloadError(`Failed to download report for ${report.company_name}: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteReport = async (reportId) => {
    setConfirmModal({ title:"Delete Report?", message:"This permanently removes the report and its saved Excel file from the server.", onConfirm: async ()=>{ try{ await deleteAdminReport(reportId); setReports(prev => prev.filter(r => r.id !== reportId)); }catch(e){ setDownloadError(e.message); } } });
  };

  const refreshBankStatements = async () => {
    setBankStatementsLoading(true);
    try {
      const result = await getAdminBankStatements();
      setBankStatements(result);
    } catch (err) {
      setBankDownloadError(err.message || "Could not load bank statements.");
    } finally {
      setBankStatementsLoading(false);
    }
  };

  const downloadBankStatement = async (stmt) => {
    setBankDownloadingId(stmt.id);
    setBankDownloadError("");
    try {
      if (!stmt.excel_exists) {
        throw new Error("No Excel file was found on the server for this statement.");
      }
      const fallback = `BankStatement_${stmt.bank_display || stmt.bank}_${(stmt.company_name||stmt.username||"client").replace(/\s+/g,"_")}_${stmt.id}.xlsx`;
      await downloadAdminBankExcel(stmt.id, bankStatementFilename(stmt.filename_hint, fallback));
    } catch (err) {
      setBankDownloadError(`Failed to download statement #${stmt.id}: ${err.message}`);
    } finally {
      setBankDownloadingId(null);
    }
  };

  const planBadge = p => {
    if (p==="Paid")  return <span className="badge b-paid"><span className="badge-dot"/>Paid</span>;
    if (p==="Trial") return <span className="badge b-trial"><span className="badge-dot"/>Trial</span>;
    return <span className="badge b-inactive"><span className="badge-dot"/>{p}</span>;
  };

  const statusBadge = s => s==="Active"
    ? <span className="badge b-active"><span className="badge-dot"/>Active</span>
    : <span className="badge b-inactive"><span className="badge-dot"/>Inactive</span>;

  return (
    <>
      <style>{styles}</style>
      {adminError && <div className="msg error" style={{margin:16}}>{adminError}</div>}

      {/* Add Client Modal */}
      {showModal&&(
        <div className="modal-overlay" onClick={e=>{if(e.target.classList.contains("modal-overlay"))setModal(false)}}>
          <div className="modal">
            <div className="modal-title">Add New Client</div>
            <div className="field"><label>Business Name</label><input placeholder="e.g. Sithobela Supermarket" value={nName} onChange={e=>setNName(e.target.value)}/></div>
            <div className="field"><label>Email Address</label><input type="email" placeholder="e.g. admin@business.sz" value={nEmail} onChange={e=>setNEmail(e.target.value)}/></div>
            <div className="field"><label>Cell Number</label><input type="tel" placeholder="e.g. +268 7612 3456" value={nCell} onChange={e=>setNCell(e.target.value)}/></div>
            <div className="field"><label>Plan</label>
              <select value={nPlan} onChange={e=>setNPlan(e.target.value)}>
                <option>Trial</option><option>Paid</option>
              </select>
            </div>
            <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>An API key will be automatically generated.</div>
            <div className="modal-actions">
              <button className="btn btn-blue" style={{flex:1}} onClick={addClient} disabled={!nName||!nEmail}>Create Client</button>
              <button className="btn btn-ghost" style={{flex:1}} onClick={()=>setModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* View Client Modal */}
      {viewClient&&(
        <div className="modal-overlay" onClick={e=>{if(e.target.classList.contains("modal-overlay"))setView(null)}}>
          <div className="modal">
            <div className="modal-title">{viewClient.name}</div>
            <div className="v-row"><span className="v-lbl">Email</span><span className="v-val">{viewClient.email}</span></div>
            <div className="v-row"><span className="v-lbl">Cell Number</span><span className="v-val t-mono">{viewClient.cell||"—"}</span></div>
            <div className="v-row"><span className="v-lbl">Plan</span><span>{planBadge(viewClient.plan)}</span></div>
            <div className="v-row"><span className="v-lbl">Status</span><span>{statusBadge(viewClient.status)}</span></div>
            <div className="v-row"><span className="v-lbl">Invoices Processed</span><span className="v-val c-blue">{viewClient.invoices}</span></div>
            <div className="v-row"><span className="v-lbl">Client Since</span><span className="v-val">{viewClient.joined}</span></div>
            <div style={{marginTop:14}}>
              <div style={{fontSize:10,fontWeight:600,color:"var(--muted)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>Registered Users</div>
              {users.filter(u=>u.company_id===viewClient.id).length===0
                ? <div style={{fontSize:12,color:"var(--muted)",fontStyle:"italic"}}>No users registered yet for this company.</div>
                : <div style={{fontSize:12}}>
                    {users.filter(u=>u.company_id===viewClient.id).map(u=>(
                      <div key={u.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid var(--border)"}}>
                        <div><strong style={{color:"var(--text)"}}>{u.username}</strong><br/><span style={{color:"var(--muted)",fontSize:11}}>{u.email||"No email"}</span></div>
                        <div style={{textAlign:"right"}}>
                          <span style={{display:"inline-block",padding:"2px 8px",background:"rgba(34,211,168,0.1)",color:"var(--success)",borderRadius:4,fontSize:11,fontWeight:600}}>
                            {u.key_active?"Active":"Paused"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
              }
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" style={{width:"100%"}} onClick={()=>setView(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Client Modal ── */}
      {editClient&&(
        <div className="modal-overlay" onClick={e=>{if(e.target.classList.contains("modal-overlay"))setEditClient(null)}}>
          <div className="modal">
            <div className="modal-title">Edit Client — {editClient.name}</div>
            <div className="field"><label>Business Name</label>
              <input value={ecName} onChange={e=>setEcName(e.target.value)}/>
            </div>
            <div className="field"><label>Email Address</label>
              <input type="email" value={ecEmail} onChange={e=>setEcEmail(e.target.value)}/>
            </div>
            <div className="field"><label>Cell Number</label>
              <input type="tel" placeholder="+268 7612 3456" value={ecCell} onChange={e=>setEcCell(e.target.value)}/>
            </div>
            <div className="field"><label>Plan</label>
              <select value={ecPlan} onChange={e=>setEcPlan(e.target.value)}>
                <option>Trial</option><option>Paid</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-blue" style={{flex:1}} onClick={saveEditClient}>Save Changes</button>
              <button className="btn btn-ghost" style={{flex:1}} onClick={()=>setEditClient(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add User Modal ── */}
      {showUserModal&&(
        <div className="modal-overlay" onClick={e=>{if(e.target.classList.contains("modal-overlay")){setUserModal(false);setUserError("");}}}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-title">Add User / API Key</div>
            {userError&&<div style={{background:"rgba(255,107,138,0.08)",border:"1px solid rgba(255,107,138,0.25)",color:"var(--error)",padding:"10px 14px",borderRadius:10,fontSize:13,marginBottom:14}}>⚠ {userError}</div>}
            <div className="field"><label>Company</label>
              <select value={uCompany} onChange={e=>setUCompany(e.target.value)}>
                <option value="">— Select company —</option>
                {clients.filter(c=>c.status==="Active").map(c=>(
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="field"><label>Username <span style={{color:"var(--error)"}}>*</span></label>
              <input placeholder="e.g. john.dlamini" value={uUsername} onChange={e=>setUUsername(e.target.value)}/>
            </div>
            <div className="field"><label>Email (optional)</label>
              <input type="email" placeholder="john@company.sz" value={uEmail} onChange={e=>setUEmail(e.target.value)}/>
            </div>
            <div className="field"><label>Login Password</label>
              <input type="password" placeholder="client123" value={uPassword} onChange={e=>setUPassword(e.target.value)}/>
            </div>
            <div className="field"><label>Extraction Plan</label>
              <select value={uExtractionPlan} onChange={e=>setUExtractionPlan(e.target.value)}>
                <option value="Basic">Basic — Standard extraction</option>
                <option value="Pro">Pro — Auto re-extract flagged invoices</option>
              </select>
            </div>
            {uExtractionPlan==="Pro"&&(
              <div style={{fontSize:11,padding:"8px 12px",background:"rgba(59,158,255,0.06)",border:"1px solid rgba(59,158,255,0.2)",borderRadius:8,color:"var(--ai-blue)",marginBottom:12}}>
                ✨ <strong>Pro:</strong> LOW confidence and grey-flagged invoices are automatically sent for a deeper AI re-extraction behind the scenes.
              </div>
            )}
            <div style={{height:1,background:"var(--border)",margin:"16px 0"}}/>
            <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>Gemini API Key</div>
            <div className="field"><label>Key Label</label>
              <input placeholder="e.g. Primary Key" value={uKeyLabel} onChange={e=>setUKeyLabel(e.target.value)}/>
            </div>
            <div className="field"><label>Paste Gemini API Key</label>
              <input placeholder="AIza..." value={uApiKey} onChange={e=>setUApiKey(e.target.value)} style={{fontFamily:"monospace",fontSize:12}}/>
            </div>
            <div style={{fontSize:11,color:"var(--muted)",marginBottom:4}}>The key is stored in <span style={{fontFamily:"monospace",color:"var(--ai-blue)"}}>api_data/users.json</span> and used automatically when this user processes invoices.</div>
            <div className="modal-actions">
              <button className="btn btn-blue" style={{flex:1}} onClick={addUser} disabled={!uUsername||!uCompany}>Create User</button>
              <button className="btn btn-ghost" style={{flex:1}} onClick={()=>{setUserModal(false);setUserError("");}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit API Key Modal ── */}
      {editKeyUser&&(
        <div className="modal-overlay" onClick={e=>{if(e.target.classList.contains("modal-overlay"))setEditKeyUser(null)}}>
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-title">Update API Key — {editKeyUser.username}</div>
            <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>Company: <strong style={{color:"var(--text)"}}>{editKeyUser.company_name}</strong></div>
            <div className="field"><label>Key Label</label>
              <input placeholder="e.g. Primary Key" value={ekLabel} onChange={e=>setEkLabel(e.target.value)}/>
            </div>
            <div className="field"><label>Gemini API Key</label>
              <input placeholder="AIza..." value={ekKey} onChange={e=>setEkKey(e.target.value)} style={{fontFamily:"monospace",fontSize:12}}/>
            </div>
            <div className="modal-actions">
              <button className="btn btn-blue" style={{flex:1}} onClick={saveEditKey}>Save Key</button>
              <button className="btn btn-ghost" style={{flex:1}} onClick={()=>setEditKeyUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className={`sidebar-overlay ${sideOpen?"open":""}`} onClick={()=>setSide(false)}/>
      <div className="app">
        {/* Sidebar */}
        <aside className={`sidebar ${sideOpen?"open":""}`}>
          <div className="sb-brand">
            <div className="sb-name">VATICA AI</div>
            <div className="sb-tag">Admin Console</div>
            <div className="sb-admin-badge"><span className="sb-dot"/>ADMINISTRATOR</div>
          </div>
          <nav className="sb-nav">
            <div className="nav-section">Management</div>
            {ADMIN_TABS.map(t=>(
              <div key={t.id} className={`nav-item ${tab===t.id?"active":""}`}
                onClick={()=>{setTab(t.id);setSide(false);}}>
                <span className="nav-icon">{t.icon}</span>{t.label}
              </div>
            ))}
          </nav>
          <div className="sb-footer">
            <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>Sigwane</div>
            <div style={{fontSize:11,color:"var(--error)",marginBottom:12}}>Full access</div>
            <button className="logout-btn" onClick={()=>onLogout?.()}>← Sign out</button>
          </div>
        </aside>

        {/* Main */}
        <div className="main">
          <header className="topbar">
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <button className="hamburger" onClick={()=>setSide(s=>!s)}>☰</button>
              <span className="topbar-title">{ADMIN_TABS.find(t=>t.id===tab)?.label}</span>
            </div>
            <div className="topbar-right">
              <button className="btn btn-blue btn-sm" onClick={()=>onViewModeChange?.("client-view")}>👁 View as Client</button>
            </div>
          </header>

          {/* ════ OVERVIEW ════ */}
          {tab==="overview"&&(
            <div className="page">
              <div className="stats-grid">
                <div className="stat-card"><div className="stat-label">Total Clients</div><div className="stat-value c-blue">{clients.length}</div><div className="stat-sub">{activeClients} active</div></div>
                <div className="stat-card"><div className="stat-label">Paid Clients</div><div className="stat-value c-green">{paidClients}</div><div className="stat-sub">{clients.length-paidClients} on trial</div></div>
                <div className="stat-card"><div className="stat-label">Invoices Processed</div><div className="stat-value c-warn">{totalInvoices.toLocaleString()}</div><div className="stat-sub">All clients · all time</div></div>
                <div className="stat-card"><div className="stat-label">Active API Keys</div><div className="stat-value c-purple">{activeKeys}/{users.length}</div><div className="stat-sub">{users.filter(u=>!u.openai_key).length > 0 ? `${users.filter(u=>!u.openai_key).length} user(s) missing key` : "All keys configured"}</div></div>
              </div>

              <div className="two-col">
                {/* Real registered users summary */}
                <div className="panel">
                  <div className="panel-header"><span className="panel-title">Registered Users</span><span style={{fontSize:11,color:"var(--muted)"}}>{users.length} total</span></div>
                  <div className="panel-body">
                    {users.length === 0
                      ? <div style={{fontSize:13,color:"var(--muted)",textAlign:"center",padding:"20px 0"}}>No users registered yet.<br/><span style={{fontSize:11}}>Go to API Keys → Add User</span></div>
                      : users.map(u => (
                          <div className="activity-item" key={u.id}>
                            <div className="act-dot" style={{background: u.key_active ? "var(--success)" : "var(--error)"}}/>
                            <div>
                              <div className="act-text" style={{fontWeight:600,color:"var(--text)"}}>{u.username}</div>
                              <div className="act-time">{u.company_name} · {u.key_active ? "Active" : "Paused"} · {u.openai_key ? "Key set ✓" : "⚠ No key"}</div>
                            </div>
                          </div>
                        ))
                    }
                  </div>
                </div>

                {/* Client usage */}
                <div className="panel">
                  <div className="panel-header"><span className="panel-title">Client Usage</span><span style={{fontSize:11,color:"var(--ai-blue)",cursor:"pointer"}} onClick={()=>setTab("pagecount")}>Full page count →</span></div>
                  <div className="panel-body">
                    {clients.filter(c=>c.status==="Active").map(c=>(
                      <div key={c.id} style={{marginBottom:14}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{c.name.split(" ").slice(0,2).join(" ")}</span>
                          <span style={{fontSize:12,fontFamily:"monospace",color:"var(--ai-blue)"}}>{c.invoices||c.usage||0} inv</span>
                        </div>
                        <div className="usage-bar-bg">
                          <div className="usage-bar-fill" style={{width:`${Math.min((c.invoices||c.usage||0),100)}%`,background:`linear-gradient(90deg, #1a6fd4, #3b9eff)`}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Client quick table */}
              <div className="panel full-col">
                <div className="panel-header">
                  <span className="panel-title">All Clients</span>
                  <span style={{fontSize:12,color:"var(--ai-blue)",cursor:"pointer"}} onClick={()=>setTab("clients")}>Manage all →</span>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table className="tbl"><thead><tr>
                    <th>Business</th><th>Email</th><th>Plan</th><th>Status</th><th>Invoices</th><th>Actions</th>
                  </tr></thead><tbody>
                    {clients.slice(0,4).map(c=>(
                      <tr key={c.id}>
                        <td><span className="t-name">{c.name}</span></td>
                        <td>{c.email}</td>
                        <td>{planBadge(c.plan)}</td>
                        <td>{statusBadge(c.status)}</td>
                        <td><span className="t-blue">{c.invoices}</span></td>
                        <td>
                          <div style={{display:"flex",gap:6}}>
                            <button className="act-btn act-view" onClick={()=>setView(c)}>View</button>
                            <button className={`act-btn ${c.status==="Active"?"act-disable":"act-enable"}`} onClick={()=>toggleStatus(c.id)}>
                              {c.status==="Active"?"Disable":"Enable"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              </div>
            </div>
          )}

          {/* ════ CLIENTS ════ */}
          {tab==="clients"&&(
            <div className="page">
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
                <button className="btn btn-blue btn-sm" onClick={()=>setModal(true)}>+ Add New Client</button>
              </div>
              <div className="panel full-col">
                <div className="panel-header"><span className="panel-title">All Clients ({clients.length})</span></div>
                <div style={{overflowX:"auto"}}>
                  <table className="tbl"><thead><tr>
                    <th>#</th><th>Business Name</th><th>Email</th><th>Cell No</th><th>Plan</th><th>Status</th><th>Invoices</th><th>Joined</th><th>Actions</th>
                  </tr></thead><tbody>
                    {clients.map((c,i)=>(
                      <tr key={c.id}>
                        <td style={{color:"var(--muted)"}}>{i+1}</td>
                        <td><span className="t-name">{c.name}</span></td>
                        <td style={{fontSize:12}}>{c.email}</td>
                        <td><span className="t-mono">{c.cell||"—"}</span></td>
                        <td>{planBadge(c.plan)}</td>
                        <td>{statusBadge(c.status)}</td>
                        <td><span className="t-blue">{c.invoices}</span></td>
                        <td style={{fontSize:12,color:"var(--muted)"}}>{c.joined}</td>
                        <td>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            <button className="act-btn act-view" onClick={()=>setView(c)}>View</button>
                            <button className="act-btn act-edit" onClick={()=>openEditClient(c)}>Edit</button>
                            <button className={`act-btn ${c.status==="Active"?"act-disable":"act-enable"}`} onClick={()=>toggleStatus(c.id)}>
                              {c.status==="Active"?"Disable":"Enable"}
                            </button>
                            <button className="act-btn" style={{color:"var(--error)",borderColor:"rgba(255,107,138,0.3)"}} onClick={()=>deleteClient(c.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              </div>
            </div>
          )}

          {/* ════ API KEYS ════ */}
          {tab==="apikeys"&&(
            <div className="page">
              {/* Stats row */}
              <div className="stats-grid" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
                <div className="stat-card"><div className="stat-label">Total Users</div><div className="stat-value c-blue">{users.length}</div><div className="stat-sub">Across all companies</div></div>
                <div className="stat-card"><div className="stat-label">Active Keys</div><div className="stat-value c-green">{users.filter(u=>u.key_active).length}</div><div className="stat-sub">Processing enabled</div></div>
                <div className="stat-card"><div className="stat-label">Paused</div><div className="stat-value c-warn">{users.filter(u=>!u.key_active).length}</div><div className="stat-sub">Suspended users</div></div>
                <div className="stat-card"><div className="stat-label">Companies</div><div className="stat-value c-purple">{new Set(users.map(u=>u.company_id)).size}</div><div className="stat-sub">With registered users</div></div>
              </div>

              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
                <button className="btn btn-blue btn-sm" onClick={()=>setUserModal(true)}>+ Add User / API Key</button>
              </div>

              <div className="panel full-col">
                <div className="panel-header">
                  <span className="panel-title">Users & API Keys</span>
                  <span style={{fontSize:12,color:"var(--muted)"}}>Each user has their own Gemini key · linked by username at login</span>
                </div>
                {users.length===0
                  ? <div style={{padding:"40px 20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>
                      <div style={{fontSize:32,marginBottom:10,opacity:0.4}}>🔑</div>
                      No users yet. Click <strong style={{color:"var(--ai-blue)"}}>+ Add User / API Key</strong> to register the first user.
                    </div>
                  : <div style={{overflowX:"auto"}}>
                      <table className="tbl"><thead><tr>
                        <th>Username</th><th>Company</th><th>Email</th><th>Key Label</th><th>API Key (masked)</th><th>Status</th><th>Added</th><th>Actions</th>
                      </tr></thead><tbody>
                        {users.map(u=>(
                          <tr key={u.id}>
                            <td><span className="t-name">{u.username}</span></td>
                            <td style={{fontSize:12,color:"var(--muted-light)"}}>{u.company_name}</td>
                            <td style={{fontSize:12}}>{u.email||"—"}</td>
                            <td><span style={{fontSize:12,color:"var(--ai-blue)"}}>{u.key_label||"Primary Key"}</span></td>
                  <td><span style={{
                    display:"inline-flex",alignItems:"center",gap:4,
                    padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600,
                    background: u.extraction_plan==="Pro" ? "rgba(124,58,237,0.12)" : "rgba(59,158,255,0.08)",
                    color: u.extraction_plan==="Pro" ? "var(--purple)" : "var(--ai-blue)",
                    border: u.extraction_plan==="Pro" ? "1px solid rgba(124,58,237,0.25)" : "1px solid rgba(59,158,255,0.2)"
                  }}>{u.extraction_plan==="Pro" ? "✨ Pro" : "Basic"}</span></td>
                            <td>
                              {u.openai_key
                                ? <div className="key-box" style={{marginBottom:0,maxWidth:260}}>
                                    <span className="key-val" style={{fontSize:11}}>
                                      {u.openai_key.slice(0,10)}{"•".repeat(14)}{u.openai_key.slice(-4)}
                                    </span>
                                    <button className="copy-btn" onClick={()=>copyKey(u.openai_key,u.id)}>
                                      {copied===u.id?"✓":"Copy"}
                                    </button>
                                  </div>
                                : <span style={{color:"var(--muted)",fontSize:12}}>No key set</span>
                              }
                            </td>
                            <td>
                              {u.key_active
                                ? <span className="badge b-active"><span className="badge-dot"/>Active</span>
                                : <span className="badge b-inactive"><span className="badge-dot"/>Paused</span>}
                            </td>
                            <td style={{fontSize:12,color:"var(--muted)"}}>{u.created_at||"—"}</td>
                            <td>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                <button className="act-btn act-edit" onClick={()=>openEditKey(u)}>✏ Key</button>
                                <button className={`act-btn ${u.key_active?"act-disable":"act-enable"}`} onClick={()=>pauseUser(u.id)}>
                                  {u.key_active?"Pause":"Resume"}
                                </button>
                                <button className="act-btn" style={{color:"var(--error)",borderColor:"rgba(255,107,138,0.3)"}}
                                  onClick={()=>deleteUser(u.id,u.username)}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                }
              </div>

              {/* Group by company */}
              {users.length>0&&(
                <div className="panel full-col">
                  <div className="panel-header"><span className="panel-title">Users by Company</span></div>
                  <div className="panel-body">
                    {clients.filter(c=>users.some(u=>u.company_id===c.id)).map(c=>{
                      const cUsers=users.filter(u=>u.company_id===c.id);
                      return(
                        <div key={c.id} style={{marginBottom:20}}>
                          <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                            {c.name}
                            <span style={{fontSize:11,color:"var(--muted)",fontWeight:400}}>({cUsers.length} user{cUsers.length!==1?"s":""})</span>
                          </div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                            {cUsers.map(u=>(
                              <div key={u.id} style={{background:"rgba(59,158,255,0.06)",border:`1px solid ${u.key_active?"rgba(34,211,168,0.2)":"rgba(255,107,138,0.2)"}`,borderRadius:10,padding:"8px 14px",fontSize:12}}>
                                <div style={{fontWeight:600,color:"var(--text)",marginBottom:2}}>{u.username}</div>
                                <div style={{color:"var(--muted)",fontSize:11}}>{u.key_label||"Primary Key"} · {u.key_active?"Active":"Paused"}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="panel">
                <div className="panel-header"><span className="panel-title">How It Works</span></div>
                <div className="panel-body">
                  {[
                    ["Login link","Username on login form → looks up user in users.json → injects their Gemini key"],
                    ["Key injection","When a user processes invoices or bank statements, their personal Gemini key is used automatically"],
                    ["Pause","Pausing blocks login and disables key — user sees a clear error message"],
                    ["Delete","Removes the user from the system permanently — they cannot log in"],
                    ["Storage","Keys are stored in api_data/users.json — never exposed in the browser"],
                  ].map(([l,v])=>(
                    <div className="v-row" key={l}>
                      <span className="v-lbl" style={{minWidth:110}}>{l}</span>
                      <span style={{fontSize:12,color:"var(--muted-light)",flex:1}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ════ BILLING ════ */}
          {tab==="billing"&&(
            <div className="page">

              {/* Month selector + summary stats */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--muted-light)"}}>Billing period:</div>
                <input type="month" value={billingMonth}
                  onChange={e=>loadBilling(e.target.value)}
                  style={{padding:"7px 12px",background:"var(--panel)",border:"1px solid var(--border)",borderRadius:9,color:"var(--text)",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}}/>
                <div style={{fontSize:11,color:"var(--muted)"}}>$1 = E18 · $0.0071/page · Standard: E1,600 for 0–1,400 pages</div>
              </div>

              {!billing
                ? <div style={{textAlign:"center",padding:"60px 0",color:"var(--muted)",fontSize:13}}>Loading billing data…</div>
                : <>
                  {/* Grand totals row */}
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-label">Total Invoices</div>
                      <div className="stat-value c-blue">{billing.grand_invoices}</div>
                      <div className="stat-sub">All clients · {billing.month}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Total Pages</div>
                      <div className="stat-value c-warn">{billing.grand_pages.toLocaleString()}</div>
                      <div className="stat-sub">Extracted this period</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Total API Cost</div>
                      <div className="stat-value c-err">${billing.grand_api_usd.toFixed(2)}</div>
                      <div className="stat-sub">≈ E{billing.grand_api_e.toFixed(2)}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Paid Clients</div>
                      <div className="stat-value c-green">{paidClients}</div>
                      <div className="stat-sub">Active subscriptions</div>
                    </div>
                  </div>

                  {/* Per-client billing cards */}
                  {billing.clients.filter(c=>c.status!=="deleted").map(c=>{
                    const tier = c.tier || {};
                    const hasUsage = c.total_pages > 0;
                    return (
                      <div key={c.id} className="panel full-col" style={{marginBottom:16}}>
                        {/* Client header */}
                        <div style={{padding:"14px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                          <div style={{display:"flex",alignItems:"center",gap:12}}>
                            <span className="t-name" style={{fontSize:14}}>{c.name}</span>
                            {planBadge(c.plan)}
                            {statusBadge(c.status)}
                          </div>
                          <div style={{display:"flex",gap:20,fontSize:12,color:"var(--muted-light)"}}>
                            <span>📄 {c.total_invoices} invoices</span>
                            <span>📃 {c.total_pages} pages</span>
                            <span style={{color:"var(--error)"}}>API: ${c.total_api_cost_usd.toFixed(2)} = E{c.total_api_cost_e.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Tier / subscription box */}
                        <div style={{padding:"12px 20px",borderBottom:"1px solid var(--border)",background:"rgba(59,158,255,0.03)",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                          <div style={{fontSize:12}}>
                            <span style={{color:"var(--muted)"}}>Subscription tier: </span>
                            <strong style={{color: tier.extra_blocks > 0 ? "var(--warning)" : "var(--success)"}}>{tier.tier || "Standard"}</strong>
                            <span style={{color:"var(--muted)",marginLeft:8}}>
                              ({c.total_pages} / {tier.pages_included} pages included)
                            </span>
                            {tier.extra_blocks > 0 && (
                              <span style={{color:"var(--warning)",marginLeft:8}}>
                                +{tier.extra_blocks} extra block{tier.extra_blocks>1?"s":""} × E200 = +E{tier.extra_cost_e}
                              </span>
                            )}
                          </div>
                          <div style={{fontFamily:"monospace",fontWeight:700,fontSize:14,color: c.plan==="Paid" ? "var(--success)" : "var(--warning)"}}>
                            {c.plan==="Paid" ? `E ${tier.total_fee_e?.toLocaleString("en",{minimumFractionDigits:2})}` : "Trial — E 0.00"}
                          </div>
                        </div>

                        {/* Per-user rows */}
                        {c.users.length === 0
                          ? <div style={{padding:"16px 20px",fontSize:12,color:"var(--muted)"}}>No users registered under this company.</div>
                          : <div style={{overflowX:"auto"}}>
                              <table className="tbl">
                                <thead><tr>
                                  <th>User</th>
                                  <th>Status</th>
                                  <th>Invoices</th>
                                  <th style={{whiteSpace:"nowrap"}}>Total Pages</th>
                                  <th style={{whiteSpace:"nowrap",color:"var(--ai-blue)"}}>📄 Purchase</th>
                                  <th style={{whiteSpace:"nowrap",color:"var(--success)"}}>🧾 Sales</th>
                                  <th style={{whiteSpace:"nowrap",color:"var(--warning)"}}>🏦 Bank</th>
                                  <th>API Cost (E)</th>
                                </tr></thead>
                                <tbody>
                                  {c.users.map(u=>(
                                    <tr key={u.user_id}>
                                      <td><span className="t-name">{u.username}</span></td>
                                      <td>
                                        {u.key_active
                                          ? <span className="badge b-active"><span className="badge-dot"/>Active</span>
                                          : <span className="badge b-inactive"><span className="badge-dot"/>Paused</span>}
                                      </td>
                                      <td><span className="t-blue">{u.invoices}</span></td>
                                      <td><span style={{fontFamily:"monospace",fontWeight:700}}>{u.pages}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--ai-blue)"}}>{u.purchase_pages||0}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--success)"}}>{u.sales_pages||0}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--warning)"}}>{u.bank_pages||0}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--warning)"}}>E {u.api_cost_e.toFixed(2)}</span></td>
                                    </tr>
                                  ))}
                                  {/* Company totals row */}
                                  {c.users.length > 1 && (
                                    <tr style={{background:"rgba(59,158,255,0.06)"}}>
                                      <td colSpan={2} style={{fontWeight:700,color:"var(--text)"}}>Company Total</td>
                                      <td><span className="t-blue" style={{fontWeight:700}}>{c.total_invoices}</span></td>
                                      <td><span style={{fontFamily:"monospace",fontWeight:700}}>{c.total_pages}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--ai-blue)",fontWeight:700}}>{c.users.reduce((s,u)=>s+(u.purchase_pages||0),0)}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--success)",fontWeight:700}}>{c.users.reduce((s,u)=>s+(u.sales_pages||0),0)}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--warning)",fontWeight:700}}>{c.users.reduce((s,u)=>s+(u.bank_pages||0),0)}</span></td>
                                      <td><span style={{fontFamily:"monospace",color:"var(--warning)",fontWeight:700}}>E {c.total_api_cost_e.toFixed(2)}</span></td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                        }
                      </div>
                    );
                  })}

                  {/* Billing tier guide */}
                  <div className="panel full-col">
                    <div className="panel-header"><span className="panel-title">Billing Tier Guide</span></div>
                    <div className="panel-body">
                      {[
                        ["0 – 1,400 pages","E 1,600.00","Standard fee — included in base subscription"],
                        ["1,401 – 2,400 pages","E 1,800.00","Standard + 1 extra block (+$10 API = E180 + E20 fee)"],
                        ["2,401 – 3,400 pages","E 2,000.00","Standard + 2 extra blocks (+$20 API = E360 + E40 fee)"],
                        ["3,401 – 4,400 pages","E 2,200.00","Standard + 3 extra blocks (+$30 API = E540 + E60 fee)"],
                        ["Every +1,000 pages above","+ E 200.00","$10 API cost = E180 + E20 admin fee per block"],
                      ].map(([pages,fee,note])=>(
                        <div className="v-row" key={pages}>
                          <span style={{fontFamily:"monospace",fontSize:12,color:"var(--ai-blue)",minWidth:200}}>{pages}</span>
                          <span style={{fontFamily:"monospace",fontWeight:700,color:"var(--success)",minWidth:110}}>{fee}</span>
                          <span style={{fontSize:12,color:"var(--muted-light)"}}>{note}</span>
                        </div>
                      ))}
                      <div style={{marginTop:14,fontSize:11,color:"var(--muted)",lineHeight:1.7}}>
                        API pricing: $0.0071 per page · Exchange rate: $1 = E18 · Monthly standard fee includes up to 1,400 pages across all users under a company.
                      </div>
                    </div>
                  </div>
                </>
              }
            </div>
          )}
          {/* ════ PAGE COUNT ════ */}
          {tab==="pagecount"&&<AdminPageCount/>}

          {/* ════ REPORTS ════ */}
          {tab==="reports"&&(
            <div className="page">
              {downloadError&&(
                <div style={{background:"rgba(255,107,138,0.08)",border:"1px solid rgba(255,107,138,0.25)",
                  color:"var(--error)",padding:"10px 16px",borderRadius:10,fontSize:13,marginBottom:16}}>
                  ⚠ {downloadError}
                </div>
              )}

              {/* Summary stats */}
              <div className="stats-grid" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
                <div className="stat-card">
                  <div className="stat-label">Total Reports</div>
                  <div className="stat-value c-blue">{reports.length}</div>
                  <div className="stat-sub">All clients · all time</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Invoices</div>
                  <div className="stat-value c-green">{reports.reduce((s,r)=>s+r.invoice_count,0).toLocaleString()}</div>
                  <div className="stat-sub">Across all batches</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Unique Users</div>
                  <div className="stat-value c-warn">{new Set(reports.map(r=>r.username)).size}</div>
                  <div className="stat-sub">Have processed invoices</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Latest Report</div>
                  <div className="stat-value c-purple" style={{fontSize:14}}>
                    {reports.length>0 ? reports[0].created_at : "—"}
                  </div>
                  <div className="stat-sub">{reports.length>0 ? reports[0].company_name : "No reports yet"}</div>
                </div>
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <select
                    value={reportTypeFilter}
                    onChange={e=>setReportTypeFilter(e.target.value)}
                    style={{padding:"7px 12px",background:"var(--panel)",border:"1px solid var(--border)",borderRadius:9,color:"var(--text)",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}}
                  >
                    <option value="all">All types</option>
                    <option value="purchases">Purchases</option>
                    <option value="sales">Sales</option>
                    <option value="multi_receipt_purchases">Multi-Receipt Purchases</option>
                    <option value="multi_receipt_sales">Multi-Receipt Sales</option>
                    <option value="merged_purchases">Merged Purchases</option>
                    <option value="merged_sales">Merged Sales</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Search by user or company…"
                    value={reportSearch}
                    onChange={e=>setReportSearch(e.target.value)}
                    style={{padding:"7px 12px",width:220,background:"var(--panel)",border:"1px solid var(--border)",borderRadius:9,color:"var(--text)",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}}
                  />
                </div>
                <button className="btn btn-ghost btn-sm" onClick={refreshReports} disabled={reportsLoading}>
                  {reportsLoading ? "⟳ Refreshing…" : "⟳ Refresh"}
                </button>
              </div>

              <div className="panel full-col">
                <div className="panel-header">
                  <span className="panel-title">All Client Reports</span>
                  <span style={{fontSize:11,color:"var(--muted)"}}>
                    Every purchases, sales, multi-receipt, and merged Excel any client has generated — updates automatically, download any of them at any time
                  </span>
                </div>

                {(() => {
                  const filtered = reports.filter(r => {
                    const rt = r.report_type || "purchases";
                    if (reportTypeFilter !== "all" && rt !== reportTypeFilter) return false;
                    if (reportSearch.trim()) {
                      const q = reportSearch.trim().toLowerCase();
                      if (!(r.username||"").toLowerCase().includes(q) &&
                          !(r.company_name||"").toLowerCase().includes(q)) return false;
                    }
                    return true;
                  });
                  if (reports.length === 0) {
                    return (
                      <div style={{padding:"48px 20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>
                        <div style={{fontSize:36,marginBottom:10,opacity:0.35}}>📊</div>
                        No reports yet. Reports are saved automatically every time a client processes invoices, receipts, or merges Excel files.
                      </div>
                    );
                  }
                  if (filtered.length === 0) {
                    return (
                      <div style={{padding:"48px 20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>
                        <div style={{fontSize:36,marginBottom:10,opacity:0.35}}>🔍</div>
                        No reports match this filter.
                      </div>
                    );
                  }
                  return (
                  <div style={{overflowX:"auto"}}>
                      <table className="tbl">
                        <thead><tr>
                          <th>#</th>
                          <th>Type</th>
                          <th>Company / Client</th>
                          <th>User</th>
                          <th>Date</th>
                          <th>Invoices</th>
                          <th>Excel File</th>
                          <th>Download</th>
                          <th></th>
                        </tr></thead>
                        <tbody>
                          {filtered.map((r,i)=>{
                            const meta = reportTypeMeta(r.report_type);
                            return (
                            <tr key={r.id}>
                              <td style={{color:"var(--muted)"}}>{i+1}</td>
                              <td>
                                <span style={{
                                  fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:20,
                                  color:meta.color,background:`${meta.color}1A`,border:`1px solid ${meta.color}55`,
                                  whiteSpace:"nowrap"
                                }}>{meta.label}</span>
                              </td>
                              <td>
                                <span className="t-name">{r.company_name}</span>
                                {r.filename_hint && r.company_name !== r.filename_hint && (
                                  <div style={{fontSize:11,color:"var(--muted)"}}>{r.filename_hint}</div>
                                )}
                              </td>
                              <td style={{fontSize:12,color:"var(--muted-light)"}}>{r.username}</td>
                              <td>
                                <span style={{fontFamily:"monospace",fontSize:12,color:"var(--ai-bright)"}}>
                                  {r.created_at}
                                </span>
                              </td>
                              <td>
                                <span style={{fontFamily:"monospace",fontWeight:600,color:"var(--success)"}}>
                                  {r.invoice_count}
                                </span>
                              </td>
                              <td style={{maxWidth:220}}>
                                {r.has_excel ? (
                                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                                    <span style={{fontSize:16}}>📗</span>
                                    <span style={{
                                      fontSize:12,color:"var(--success)",fontWeight:600,
                                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                      maxWidth:170
                                    }}>
                                      {`${meta.label.replace(/\s+/g,"_")}_${(r.company_name||"").replace(/\s+/g,"_")}_${r.month||r.created_at?.slice(0,7)||""}.xlsx`}
                                    </span>
                                  </div>
                                ) : (
                                  <span style={{fontSize:12,color:"var(--muted)",fontStyle:"italic"}}>Not generated yet</span>
                                )}
                              </td>
                              <td>
                                <button
                                  className="btn btn-blue btn-sm"
                                  style={{minWidth:120}}
                                  disabled={downloadingId===r.id}
                                  onClick={()=>downloadReport(r)}
                                >
                                  {downloadingId===r.id
                                    ? "⟳ Downloading…"
                                    : r.has_excel ? "⬇ Excel" : "⟳ Generate"}
                                </button>
                              </td>
                              <td>
                                <button
                                  className="btn btn-danger btn-sm"
                                  style={{padding:"5px 9px",fontSize:13}}
                                  title="Delete this report"
                                  onClick={() => handleDeleteReport(r.id)}
                                >
                                  🗑
                                </button>
                              </td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>

              {/* Group by company */}
              {reports.length>0&&(()=>{
                const byCompany = {};
                reports.forEach(r=>{
                  if(!byCompany[r.company_name]) byCompany[r.company_name]={name:r.company_name,count:0,invoices:0,latest:r.created_at};
                  byCompany[r.company_name].count++;
                  byCompany[r.company_name].invoices+=r.invoice_count;
                });
                const companies = Object.values(byCompany);
                return(
                  <div className="panel full-col" style={{marginTop:16}}>
                    <div className="panel-header"><span className="panel-title">Reports by Company</span></div>
                    <div className="panel-body" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
                      {companies.map(c=>(
                        <div key={c.name} style={{background:"rgba(59,158,255,0.05)",border:"1px solid var(--border-blue)",
                          borderRadius:12,padding:"14px 16px"}}>
                          <div style={{fontWeight:700,color:"var(--text)",marginBottom:6,fontSize:13}}>{c.name}</div>
                          <div style={{fontSize:12,color:"var(--muted-light)",marginBottom:2}}>
                            <span className="c-blue" style={{fontFamily:"monospace",fontWeight:600}}>{c.count}</span>
                            {" "}batch{c.count!==1?"es":""} ·{" "}
                            <span style={{color:"var(--success)",fontFamily:"monospace",fontWeight:600}}>{c.invoices}</span>
                            {" "}invoices
                          </div>
                          <div style={{fontSize:11,color:"var(--muted)"}}>Latest: {c.latest}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

            </div>
          )}

          {/* ════ BANK STATEMENTS ════ */}
          {tab==="bankstatements"&&(
            <div className="page">
              {bankDownloadError&&(
                <div style={{background:"rgba(255,107,138,0.08)",border:"1px solid rgba(255,107,138,0.25)",
                  color:"var(--error)",padding:"10px 16px",borderRadius:10,fontSize:13,marginBottom:16}}>
                  ⚠ {bankDownloadError}
                </div>
              )}

              {/* Summary stats */}
              <div className="stats-grid" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
                <div className="stat-card">
                  <div className="stat-label">Total Statements</div>
                  <div className="stat-value c-blue">{bankStatements.length}</div>
                  <div className="stat-sub">All clients · all time</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Transactions</div>
                  <div className="stat-value c-green">{bankStatements.reduce((s,r)=>s+(r.transaction_count||0),0).toLocaleString()}</div>
                  <div className="stat-sub">Extracted across all statements</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Unique Users</div>
                  <div className="stat-value c-warn">{new Set(bankStatements.map(r=>r.username)).size}</div>
                  <div className="stat-sub">Have uploaded statements</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Latest Statement</div>
                  <div className="stat-value c-purple" style={{fontSize:14}}>
                    {bankStatements.length>0 ? bankStatements[0].created_at : "—"}
                  </div>
                  <div className="stat-sub">{bankStatements.length>0 ? (bankStatements[0].company_name||bankStatements[0].username) : "No statements yet"}</div>
                </div>
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <select
                    value={bankFilter}
                    onChange={e=>setBankFilter(e.target.value)}
                    style={{padding:"7px 12px",background:"var(--panel)",border:"1px solid var(--border)",borderRadius:9,color:"var(--text)",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}}
                  >
                    <option value="all">All banks</option>
                    {Object.entries(BANK_LABELS).map(([slug,label])=>(
                      <option key={slug} value={slug}>{label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Search by user or company…"
                    value={bankSearch}
                    onChange={e=>setBankSearch(e.target.value)}
                    style={{padding:"7px 12px",width:220,background:"var(--panel)",border:"1px solid var(--border)",borderRadius:9,color:"var(--text)",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}}
                  />
                </div>
                <button className="btn btn-ghost btn-sm" onClick={refreshBankStatements} disabled={bankStatementsLoading}>
                  {bankStatementsLoading ? "⟳ Refreshing…" : "⟳ Refresh"}
                </button>
              </div>

              <div className="panel full-col">
                <div className="panel-header">
                  <span className="panel-title">All Client Bank Statements</span>
                  <span style={{fontSize:11,color:"var(--muted)"}}>
                    Every extracted bank statement any client has uploaded — download any of them at any time
                  </span>
                </div>

                {(() => {
                  const filtered = bankStatements.filter(r => {
                    if (bankFilter !== "all" && r.bank !== bankFilter) return false;
                    if (bankSearch.trim()) {
                      const q = bankSearch.trim().toLowerCase();
                      if (!(r.username||"").toLowerCase().includes(q) &&
                          !(r.company_name||"").toLowerCase().includes(q)) return false;
                    }
                    return true;
                  });
                  if (bankStatements.length === 0) {
                    return (
                      <div style={{padding:"48px 20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>
                        <div style={{fontSize:36,marginBottom:10,opacity:0.35}}>🏦</div>
                        No bank statements yet. They appear here as soon as a client extracts one.
                      </div>
                    );
                  }
                  if (filtered.length === 0) {
                    return (
                      <div style={{padding:"48px 20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>
                        <div style={{fontSize:36,marginBottom:10,opacity:0.35}}>🔍</div>
                        No statements match this filter.
                      </div>
                    );
                  }
                  return (
                  <div style={{overflowX:"auto"}}>
                      <table className="tbl" style={{tableLayout:"fixed"}}>
                        <colgroup>
                          <col style={{width:"4%"}} />
                          <col style={{width:"12%"}} />
                          <col style={{width:"20%"}} />
                          <col style={{width:"9%"}} />
                          <col style={{width:"15%"}} />
                          <col style={{width:"9%"}} />
                          <col style={{width:"17%"}} />
                          <col style={{width:"14%"}} />
                        </colgroup>
                        <thead><tr>
                          <th>#</th>
                          <th>Bank</th>
                          <th>Company / Client</th>
                          <th>User</th>
                          <th>Period</th>
                          <th>Transactions</th>
                          <th>Excel File</th>
                          <th>Download</th>
                        </tr></thead>
                        <tbody>
                          {filtered.map((r,i)=>(
                            <tr key={r.id}>
                              <td style={{color:"var(--muted)"}}>{i+1}</td>
                              <td>
                                <span title={r.bank_display || r.bank} style={{
                                  display:"inline-block",maxWidth:"100%",
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                  verticalAlign:"bottom",
                                  fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:20,
                                  color:"var(--warning)",background:"rgba(234,179,8,0.1)",border:"1px solid rgba(234,179,8,0.35)"
                                }}>{r.bank_display || r.bank}</span>
                              </td>
                              <td style={{overflow:"hidden"}}>
                                <div title={r.company_name || ""} className="t-name" style={{
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                                }}>{r.company_name || "—"}</div>
                                {r.filename_hint && (
                                  <div title={r.filename_hint} style={{
                                    fontSize:11,color:"var(--muted)",
                                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                                  }}>{r.filename_hint}</div>
                                )}
                              </td>
                              <td title={r.username} style={{fontSize:12,color:"var(--muted-light)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.username}</td>
                              <td title={r.period || ""} style={{fontSize:12,color:"var(--muted-light)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.period || "—"}</td>
                              <td>
                                <span style={{fontFamily:"monospace",fontWeight:600,color:"var(--success)"}}>
                                  {r.transaction_count}
                                </span>
                              </td>
                              <td style={{overflow:"hidden"}}>
                                {r.excel_exists ? (
                                  <div style={{display:"flex",alignItems:"center",gap:7,overflow:"hidden"}}>
                                    <span style={{fontSize:16,flexShrink:0}}>📗</span>
                                    <span title={bankStatementFilename(r.filename_hint, `BankStatement_${r.bank_display||r.bank}_${r.id}.xlsx`)} style={{
                                      fontSize:12,color:"var(--success)",fontWeight:600,
                                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                                    }}>
                                      {bankStatementFilename(r.filename_hint, `BankStatement_${r.bank_display||r.bank}_${r.id}.xlsx`)}
                                    </span>
                                  </div>
                                ) : (
                                  <span style={{fontSize:12,color:"var(--muted)",fontStyle:"italic"}}>Not available</span>
                                )}
                              </td>
                              <td>
                                <button
                                  className="btn btn-blue btn-sm"
                                  style={{width:"100%",padding:"6px 8px",fontSize:12,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}
                                  disabled={bankDownloadingId===r.id || !r.excel_exists}
                                  onClick={()=>downloadBankStatement(r)}
                                >
                                  {bankDownloadingId===r.id ? "⟳ …" : "⬇ Excel"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    
      <ConfirmModal state={confirmModal} onClose={() => setConfirmModal(null)} />
    </>
  );
}